<template>
  <div
    class="audio-player"
    :class="{ complete: isComplete, playing: isPlaying }"
    :test-id="'audio-' + audioId"
  >
    <div class="audio-player-content">
      <div class="audio-player-button-wrapper activity-button-wrapper" @click="playOrPause">
        <div class="audio-player-button activity-button">
          <span class="audio-player-button-inner activity-button-inner">
            <transition name="fade" mode="out-in">
              <span class="pause-icon" v-if="isPlaying" key="playing">
                <span class="glyphicon glyphicon-pause"></span>
              </span>
              <span class="complete-icon" v-else-if="isComplete" key="complete">
                <span class="glyphicon glyphicon-repeat"></span>
              </span>
              <span class="play-icon" v-else key="paused">
                <span class="glyphicon glyphicon-play"></span>
              </span>
            </transition>
          </span>
        </div>
      </div>
      <div class="activity-description">
        <slot></slot>
      </div>
    </div>
    <div class="audio-controls-wrapper">
      <div class="audio-controls">
        <span id="current-time" class="time">{{ conversion(position) }}</span>
        <span id="duration" class="time">{{ duration }}</span>
      </div>
      <input
        type="range"
        class="slider"
        :max="setSliderMax"
        :value="setSliderPosition"
        @click="seekSlider"
        @change="seekSlider"
      />
    </div>
    <br />
  </div>
</template>

<script lang="ts" setup>
import { Howl } from "howler";
import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
import { debug as d } from "debug";
import { Analytics } from "../services/analytics/analytics";

const debug = d("cs.AudioPlayerComponent");

interface AudioSession {
  audioId: string;
  sound: Howl;
}

let currentAudio: AudioSession | null = null;

const props = defineProps<{
  audioId: string;
  cmsAudioSrc: string;
  trackTitle: string;
  albumTitle: string;
  isComplete: boolean;
  onListenEnd: () => void;
}>();

const sound = ref<Howl | null>(null);
const isPlaying = ref(false);
const hasBeenPlayed = ref(false);
const hasJustEnded = ref(false);
const position = ref<number | Howl>(0);
let intervalId;

watch(isPlaying, (newIsPlaying) => {
  if (intervalId !== undefined) {
    clearInterval(intervalId);
  }
  if (newIsPlaying) {
    intervalId = setInterval(() => {
      position.value = sound.value?.seek() ?? 0;
    }, 1000);
  }
});

const onPlay = () => {
  debug(`onPlay - ${props.audioId}`);
  isPlaying.value = true;
};

const onPause = () => {
  debug(`onPause - ${props.audioId}`);
  isPlaying.value = false;
};

const onEnd = () => {
  debug(`onEnd - ${props.audioId}`);
  Analytics.action("Finished Audio", "AudioPlayer", {
    audioId: props.audioId,
    audioTrackTitle: props.trackTitle,
  });
  isPlaying.value = false;
  hasJustEnded.value = true;
  if (props.onListenEnd) {
    props.onListenEnd();
  }
};

const onPlayError = () => {
  debug(`onPlayError - ${props.audioId}`);
  isPlaying.value = false;
};

const positionHandler = () => {
  if (props.isComplete) {
    position.value = Math.floor(sound.value?.duration() ?? 0);
  }
};

const updateTime = () => {
  if (!sound.value?.seek()) {
    position.value = sound.value?.seek() ?? 0;
  }
};

onMounted(async () => {
  debug("Running mounted");
  await new Promise<void>((resolve, reject) => {
    sound.value = new Howl({
      src: [props.cmsAudioSrc],
      html5: true,
      format: ["mp3"],
      onplay: onPlay,
      onload: () => {
        resolve();
      },
      onseek: updateTime,
      onplayerror: onPlayError,
      onloaderror: (soundId, err) => {
        reject(err);
      },
      onpause: onPause,
      onend: onEnd,
    });
  });
  debug("Finished mounted handler");
  positionHandler();
});

onBeforeUnmount(() => {
  debug("beforeDestroy");
  if (sound.value) {
    sound.value.unload();
    if (currentAudio && currentAudio.audioId === props.audioId) {
      currentAudio = null;
    }
  }
});

const playOrPause = () => {
  if (!sound.value) {
    return;
  }

  if (currentAudio && currentAudio.audioId !== props.audioId && currentAudio.sound.playing()) {
    currentAudio.sound.pause();
    currentAudio.sound.seek(0);
  }

  currentAudio = {
    audioId: props.audioId,
    sound: sound.value,
  };

  if (sound.value.playing()) {
    sound.value.pause();
  } else {
    sound.value.play();
    if (!hasBeenPlayed.value) {
      Analytics.action("Played Audio", "AudioPlayer", {
        audioId: props.audioId,
        audioTrackTitle: props.trackTitle,
      });
      hasBeenPlayed.value = true;
      if (props.isComplete) {
        position.value = sound.value.duration();
      }
    }
  }
};

const seekSlider = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const eventAsNumber = parseInt(target.value, 10);
  sound.value?.seek(eventAsNumber);
  position.value = eventAsNumber;
};

const conversion = (value: number): string => {
  let minute: string | number = Math.floor(value / 60);
  minute = minute.toString().length === 1 ? `0${minute}` : minute;
  let second: string | number = Math.floor(value % 60);
  second = second.toString().length === 1 ? `0${second}` : second;
  return `${minute}:${second}`;
};

const duration = computed(() => conversion(Math.floor(sound.value?.duration() ?? 0)));

const setSliderMax = computed(() => Math.floor(sound.value?.duration() ?? 0));

const setSliderPosition = computed(() => {
  if (position.value === setSliderMax.value) {
    return setSliderMax.value;
  }
  return position.value;
});
</script>

<style lang="less" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 200ms;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.audio-player {
  .audio-player-button-wrapper {
    display: inline-block;
    position: relative;
    cursor: pointer;
    //width: 50%;
  }

  .audio-player-button {
    border-radius: 100% !important;
    font-size: 48px;
    line-height: 0;
    padding: 0px;
    padding-bottom: 8px;
    display: block;

    .audio-player-button-inner {
      position: relative;
    }
  }

  .audio-player-overlay {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    pointer-events: none;
    background-color: white;
    opacity: 0.9;
    display: none;
  }

  .audio-player-content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .activity-description {
    margin-left: 20px;
  }

  // &.complete:not(.playing) .audio-player-button {
  //   color: green;
  //   border-color: green;
  // }

  .audio-controls-wrapper {
    display: block;
    text-align: center;
    padding-top: 2rem;
    font-size: 1.3rem;
  }

  .audio-controls {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  #current-time,
  #duration {
    min-width: 50px;
  }

  .slider {
    margin-top: 0.8rem;
    -webkit-appearance: none; /* Override default CSS styles */
    appearance: none;
    width: 100%; /* Full-width */
    height: 2px; /* Specified height */
    background: #333; /* Grey background */
    outline: none; /* Remove outline */
    opacity: 0.7; /* Set transparency (for mouse-over effects on hover) */
    -webkit-transition: 0.2s; /* 0.2 seconds transition on hover */
    transition: opacity 0.2s;
    max-width: 100%;
  }

  /* Mouse-over effects */

  .slider:hover {
    opacity: 1; /* Fully shown on mouse-over */
    cursor: pointer;
  }

  /* The slider handle (use -webkit- (Chrome, Opera, Safari, Edge) and -moz- (Firefox) to override default look) */

  .slider::-webkit-slider-thumb {
    -webkit-appearance: none; /* Override default look */
    appearance: none;
    width: 15px; /* Set a specific slider handle width */
    height: 15px; /* Slider handle height */
    border-radius: 50%;
    background: #333; /* Green background */
    cursor: pointer; /* Cursor on hover */
  }

  .slider::-moz-range-thumb {
    width: 15px; /* Set a specific slider handle width */
    height: 15px; /* Slider handle height */
    border-radius: 50%;
    background: #333; /* Green background */
    cursor: pointer; /* Cursor on hover */
  }
}
</style>
